import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { get } from "../../utils/networkUtils";

const statusOptions = [
  { value: "upcoming", label: "Upcoming" },
  { value: "open", label: "Open" },
  { value: "locked", label: "Locked" },
  { value: "live", label: "Live" },
  { value: "finalised", label: "Finalised" },
];

const EditGameweekModal = ({ open, onClose, gameweek, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: "",
    status: "upcoming",
    budgetInitial: "",
    isDoubleGameweek: false,
    startsAt: null,
    endsAt: null,
    deadlineAt: null,
  });

  const [errors, setErrors] = useState({});
  const [deadlineMatchesFirstKickoff, setDeadlineMatchesFirstKickoff] =
    useState(false);
  const [deadlineLoading, setDeadlineLoading] = useState(false);
  const [deadlineError, setDeadlineError] = useState("");
  const [saving, setSaving] = useState(false);
  const deadlineRequestIdRef = useRef(0);
  const activeGameweekIdRef = useRef(gameweek?.id);
  const modalOpenRef = useRef(open);

  activeGameweekIdRef.current = gameweek?.id;
  modalOpenRef.current = open;

  useEffect(() => {
    deadlineRequestIdRef.current += 1;

    if (open && gameweek) {
      const startsAt = gameweek.startsAt ? new Date(gameweek.startsAt) : null;
      const endsAt = gameweek.endsAt ? new Date(gameweek.endsAt) : null;
      const deadlineAt = gameweek.deadlineAt
        ? new Date(gameweek.deadlineAt)
        : null;

      setFormData({
        name: gameweek.name || "",
        status: gameweek.status || "upcoming",
        budgetInitial: gameweek.budgetInitial ?? "",
        isDoubleGameweek: gameweek.isDoubleGameweek || false,
        startsAt,
        endsAt,
        deadlineAt,
      });
      setErrors({});
      setDeadlineMatchesFirstKickoff(
        Boolean(
          startsAt &&
            deadlineAt &&
            !Number.isNaN(startsAt.getTime()) &&
            !Number.isNaN(deadlineAt.getTime()) &&
            startsAt.getTime() === deadlineAt.getTime()
        )
      );
      setDeadlineLoading(false);
      setDeadlineError("");
      setSaving(false);
    }
  }, [open, gameweek]);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || deadlineLoading || !validate()) return;

    setSaving(true);

    try {
      const saved = await onSubmit({
        name: formData.name,
        status: formData.status,
        budgetInitial: formData.budgetInitial
          ? parseInt(formData.budgetInitial)
          : undefined,
        isDoubleGameweek: formData.isDoubleGameweek,
        deadlineAt: formData.deadlineAt
          ? formData.deadlineAt.toISOString()
          : null,
      });

      if (saved === true) {
        deadlineRequestIdRef.current += 1;
        onClose();
      }
    } catch (error) {
      console.error("Failed to save gameweek changes:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;

    deadlineRequestIdRef.current += 1;
    setDeadlineLoading(false);
    onClose();
  };

  const isActiveDeadlineRequest = (requestId, requestedGameweekId) =>
    requestId === deadlineRequestIdRef.current &&
    modalOpenRef.current &&
    requestedGameweekId === activeGameweekIdRef.current;

  const handleDeadlineMatchKickoffChange = async (event) => {
    const checked = event.target.checked;
    deadlineRequestIdRef.current += 1;
    setDeadlineMatchesFirstKickoff(checked);
    setDeadlineError("");

    if (!checked) {
      setFormData((prev) => ({
        ...prev,
        deadlineAt: gameweek?.deadlineAt ? new Date(gameweek.deadlineAt) : null,
      }));
      return;
    }

    if (!gameweek?.id) {
      setDeadlineMatchesFirstKickoff(false);
      setDeadlineError("Unable to identify this gameweek.");
      return;
    }

    const requestedGameweekId = gameweek.id;
    const requestId = deadlineRequestIdRef.current;
    setDeadlineLoading(true);

    try {
      const response = await get(
        `/admin/gameweek/${requestedGameweekId}/matches`
      );

      if (!isActiveDeadlineRequest(requestId, requestedGameweekId)) return;

      const matches = response?.data?.matches || [];
      const firstMatch = matches
        .filter((match) => match.kickoffAt)
        .sort((a, b) => new Date(a.kickoffAt) - new Date(b.kickoffAt))[0];

      if (!firstMatch) {
        setDeadlineMatchesFirstKickoff(false);
        setDeadlineError(
          "No assigned match with a kickoff time was found for this gameweek."
        );
        return;
      }

      const firstKickoff = new Date(firstMatch.kickoffAt);
      if (Number.isNaN(firstKickoff.getTime())) {
        setDeadlineMatchesFirstKickoff(false);
        setDeadlineError(
          "The first assigned match has an invalid kickoff time."
        );
        return;
      }

      setFormData((prev) => ({ ...prev, deadlineAt: firstKickoff }));
    } catch (error) {
      if (!isActiveDeadlineRequest(requestId, requestedGameweekId)) return;

      console.error("Failed to fetch gameweek matches:", error);
      setDeadlineMatchesFirstKickoff(false);
      setDeadlineError(
        "Failed to load the first match kickoff. Please try again."
      );
    } finally {
      if (isActiveDeadlineRequest(requestId, requestedGameweekId)) {
        setDeadlineLoading(false);
      }
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown={saving}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Gameweek</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Gameweek Name *"
                  value={formData.name}
                  onChange={handleChange("name")}
                  disabled={saving}
                  error={!!errors.name}
                  helperText={errors.name || "Name of the gameweek"}
                  placeholder="Enter gameweek name"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.status} disabled={saving}>
                  <InputLabel>Status *</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={handleChange("status")}
                    label="Status *"
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Initial Budget"
                  type="number"
                  value={formData.budgetInitial}
                  onChange={handleChange("budgetInitial")}
                  disabled={saving}
                  helperText="Team budget for this gameweek"
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Starts At"
                  value={formData.startsAt}
                  onChange={() => {}}
                  disabled
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      disabled
                      helperText={
                        formData.startsAt
                          ? "Auto-calculated from earliest match"
                          : "Will be calculated when matches are assigned"
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Ends At"
                  value={formData.endsAt}
                  onChange={() => {}}
                  disabled
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      disabled
                      helperText={
                        formData.endsAt
                          ? "Auto-calculated from latest match"
                          : "Will be calculated when matches are assigned"
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Deadline At (Transfer Deadline)"
                  value={formData.deadlineAt}
                  onChange={() => {}}
                  disabled
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      disabled
                      helperText={
                        formData.deadlineAt
                          ? "Auto-calculated from the first match"
                          : "Will be calculated when matches are assigned"
                      }
                    />
                  )}
                />
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      checked={deadlineMatchesFirstKickoff}
                      onChange={handleDeadlineMatchKickoffChange}
                      disabled={deadlineLoading || saving}
                    />
                  }
                  label={
                    deadlineLoading
                      ? "Loading first match kickoff..."
                      : "Deadline same as first match kickoff"
                  }
                />
                {deadlineError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {deadlineError}
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isDoubleGameweek}
                      disabled={saving}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          isDoubleGameweek: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Double Gameweek"
                />
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>Auto-Calculated Fields:</strong> Start time, end time,
                  and deadline are automatically calculated based on the matches
                  assigned to this gameweek and cannot be manually edited.
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>Status Guide:</strong>
                  <br />• <strong>Upcoming:</strong> Gameweek is scheduled but
                  not yet open
                  <br />• <strong>Open:</strong> Users can make transfers and
                  changes
                  <br />• <strong>Locked:</strong> Transfers are closed, matches
                  are about to start
                  <br />• <strong>Live:</strong> Matches are in progress
                  <br />• <strong>Finalised:</strong> All matches finished,
                  points calculated
                </Alert>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || deadlineLoading}
              startIcon={
                saving ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

export default EditGameweekModal;
